import {
  HHCard,
  HHIconButton,
  HHInfoGrid,
  HHStack,
} from '@hinge-health/react-component-library';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import {
  ContractPackagesType,
  SubscriptionPackageType,
  useGetAllPackagesQuery,
} from '../../types';

interface SubscriptionPackageDetailsProps {
  subscriptionPackages?: SubscriptionPackageType[];
  contractPackages?: ContractPackagesType[];
}

const SubscriptionPackageDetails = ({
  subscriptionPackages,
  contractPackages,
}: SubscriptionPackageDetailsProps): JSX.Element | null => {
  const [currentPackageIndex, setCurrentPackageIndex] = useState(0);

  const { data } = useGetAllPackagesQuery();

  if (!subscriptionPackages || subscriptionPackages.length === 0) {
    return null;
  }

  const contractPackage = contractPackages?.find(
    cp => cp.id === subscriptionPackages[currentPackageIndex].contractPackageId,
  );

  return (
    <HHCard
      sx={{
        px: 2,
        backgroundColor: theme => theme.palette.background.default,
      }}
    >
      {subscriptionPackages.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <HHStack spacing={1} direction="row">
            <HHIconButton
              hhVariant="medium"
              onClick={(): void =>
                setCurrentPackageIndex(prev =>
                  prev === 0 ? subscriptionPackages.length - 1 : prev - 1,
                )
              }
              disabled={currentPackageIndex === 0}
            >
              <ChevronLeft />
            </HHIconButton>
            <HHIconButton
              hhVariant="medium"
              onClick={(): void =>
                setCurrentPackageIndex(prev =>
                  prev === subscriptionPackages.length - 1 ? 0 : prev + 1,
                )
              }
              disabled={currentPackageIndex === subscriptionPackages.length - 1}
            >
              <ChevronRight />
            </HHIconButton>
          </HHStack>
        </Box>
      )}
      <Box sx={{ mt: subscriptionPackages.length > 1 ? 2 : 0 }}>
        <HHInfoGrid
          data={[
            {
              content: String(subscriptionPackages[currentPackageIndex].id),
              heading: 'Id',
              copyable: true,
            },
            {
              content: String(
                subscriptionPackages[currentPackageIndex].contractPackageId,
              ),
              heading: 'Contract Pkg Id',
              copyable: true,
            },
            {
              content:
                data?.getAllPackages.find(
                  pkg => pkg.id === contractPackage?.packageId,
                )?.name ?? '--',
              heading: 'Package Name',
              copyable: false,
            },
            {
              content: new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(subscriptionPackages[currentPackageIndex].packageCap),
              heading: 'Package Cap',
              copyable: false,
            },
            {
              content: dayjs(
                subscriptionPackages[currentPackageIndex].startsAt,
              ).format('L'),
              heading: 'Starts At',
              copyable: false,
            },
            {
              content:
                subscriptionPackages[currentPackageIndex].eventUuid.length > 20
                  ? `${subscriptionPackages[currentPackageIndex].eventUuid.substring(0, 20)}...`
                  : subscriptionPackages[currentPackageIndex].eventUuid,
              heading: 'Event UUID',
              copyable: true,
            },
          ]}
          divided={false}
          justifyBetween={true}
        />
      </Box>
    </HHCard>
  );
};

export default SubscriptionPackageDetails;
